import React, { useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { config } from '../config/config';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import './AddBusiness.css';

const AddBusiness = () => {
  const [businessData, setBusinessData] = useState({
    businessIdentifier: '',
    businessName: '',
    access_code: '',
    businessEmail: '',
    businessPassword: '',
    businessFirstName: '',
    businessLastName: '',
    solutionsProAccess: false,
    sessionsAvailable: ''
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const navigate = useNavigate();
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { businessIdentifier, businessName, access_code, businessEmail, businessPassword, businessFirstName, businessLastName, solutionsProAccess, sessionsAvailable } = businessData;

  const onChange = (e) => {
    const { name, value } = e.target;

    // Allow only uppercase letters for businessIdentifier
    if (name === 'businessIdentifier') {
      const uppercaseValue = value.toUpperCase();
      if (/^[A-Z]*$/.test(uppercaseValue)) {
        setBusinessData({ ...businessData, [name]: uppercaseValue });
      }
    } else {
      setBusinessData({ ...businessData, [name]: value });
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const validatePassword = (password) => {
    const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return re.test(password);
  };

  const validateForm = () => {
    const { businessIdentifier, businessName, access_code, businessEmail, businessPassword, businessFirstName, businessLastName } = businessData;

    if (!businessIdentifier.trim() || !/^[A-Z]+$/.test(businessIdentifier)) {
      toast.error('Business ID must contain only uppercase letters.');
      return false;
    }

    // Business Name: Required, text field
    if (!businessName.trim()) {
      toast.error('Business Name is required.');
      return false;
    }

    // Total Access Codes: Required, must be a number between 1 and 100
    if (!access_code.trim() || isNaN(Number(access_code)) || Number(access_code) < 1 || Number(access_code) > 100) {
      toast.error('Total Access Codes must be a number between 1 and 100.');
      return false;
    }

    // Email: Required, must be valid email
    if (!businessEmail.trim() || !validateEmail(businessEmail)) {
      toast.error('Valid Business Email is required.');
      return false;
    }

    // Sessions Available: Required when Solutions Pro Access is ON, must be a positive number
    if (solutionsProAccess) {
      if (!sessionsAvailable.toString().trim() || isNaN(Number(sessionsAvailable)) || Number(sessionsAvailable) < 1) {
        toast.error('Sessions Available must be a positive number when Solutions Pro Access is enabled.');
        return false;
      }
    }

    // Password: Required, must meet criteria
    if (!businessPassword.trim() || !validatePassword(businessPassword)) {
      toast.error('Password must meet the required criteria (min 8 chars, uppercase, lowercase, number, special character).');
      return false;
    }

    // Confirm Password: Required, must match password
    if (businessPassword !== confirmPassword) {
      toast.error('Password and Confirm Password do not match.');
      return false;
    }

    // First Name: Required
    if (!businessFirstName.trim()) {
      toast.error('First Name is required.');
      return false;
    }

    // Last Name: Required
    if (!businessLastName.trim()) {
      toast.error('Last Name is required.');
      return false;
    }

    return true;
  };


  const handleSubmit = async (event) => {
    event.preventDefault();

    // Run validations
    if (!validateForm()) return;

    try {
      console.log(businessData, "businessData")
      const payload = {
        ...businessData,
        // If solutionsProAccess is false, set sessionsAvailable to 0
        // If solutionsProAccess is true, use the provided value (convert to number)
        sessionsAvailable: businessData.solutionsProAccess 
          ? Number(businessData.sessionsAvailable) || 0 
          : 0,
        // Ensure access_code is a number
        access_code: Number(businessData.access_code),
        systemUrl: process.env.REACT_APP_BASE_URL,
      };
      const request = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/business/createBusiness`,
        payload,
        config
      );
      if (request.data.success) {
        toast.success('✅ Business profile and access code created successfully.');
        setButtonDisabled(true);
        setTimeout(() => {
          navigate('/businesses');
        }, 3000);
      } else {
        toast.error(request.data.message || 'Could not create business');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'An error occurred');
    }
  };

  return (
    <div style={pageWrapStyle}>
      <ToastContainer />
      <div style={outerCardStyle}>
        <div style={innerCardStyle}>
          <div style={sectionHeaderStyle}>
            <h3 style={sectionHeadingStyle}>Business Profile</h3>
            <div style={underlineStyle}></div>
          </div>
          <Form onSubmit={handleSubmit}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Business ID</label>
              <input
                name="businessIdentifier"
                type="text"
                maxLength={3}
                style={{ ...inputStyle, backgroundColor: '#F8FAFC' }}
                value={businessIdentifier}
                onChange={onChange}
                placeholder="Text"
                required
              />
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Business Name</label>
              <input
                name="businessName"
                type="text"
                style={{ ...inputStyle, backgroundColor: '#F8FAFC' }}
                value={businessName}
                onChange={onChange}
                placeholder="Text"
                required
              />
            </div>
            <div style={{ ...formGroupStyle, borderBottom: 'none' }}>
              <label style={labelStyle}>Total Access Codes</label>
              <input
                name="access_code"
                type="number"
                style={{ ...inputStyle, backgroundColor: '#F8FAFC' }}
                value={access_code || ''}
                onChange={onChange}
                step="1"
                min="0"
                placeholder="0"
                required
              />
            </div>
          </Form>
        </div>

        <div style={innerCardStyle}>
          <div style={{ ...sectionHeaderStyle, position: 'relative', paddingRight: '50px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <h3 style={sectionHeadingStyle}>Solutions Pro Access</h3>
            </div>
            <Form.Check
              type="switch"
              checked={solutionsProAccess}
              onChange={(e) => {
                const newValue = e.target.checked;
                // When toggle is OFF, set sessionsAvailable to 0 and clear the field
                setBusinessData({ 
                  ...businessData, 
                  solutionsProAccess: newValue,
                  sessionsAvailable: newValue ? businessData.sessionsAvailable : ''
                });
              }}
              style={{ ...toggleSwitchStyle, position: 'absolute', right: '12px', top: '12px' }}
            />
          </div>
          {solutionsProAccess && (
            <div style={{ height: '1px', backgroundColor: '#27B99A', width: 'calc(100% - 40px)', marginTop: '0px', marginLeft: '20px', marginRight: '20px', marginBottom: '10px' }}></div>
          )}
          {solutionsProAccess && (
            <div style={{ ...formGroupStyle, borderBottom: 'none' }}>
              <label style={labelStyle}>Sessions Available</label>
              <input
                name="sessionsAvailable"
                type="number"
                min="1"
                style={{ ...inputStyle, backgroundColor: '#F8FAFC' }}
                value={sessionsAvailable}
                onChange={onChange}
                placeholder="0"
                required={solutionsProAccess}
              />
            </div>
          )}
        </div>

        <div style={innerCardStyle}>
          <div style={sectionHeaderStyle}>
            <h3 style={sectionHeadingStyle}>Business Admin</h3>
            <div style={underlineStyle}></div>
          </div>
          <Form onSubmit={handleSubmit}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>First Name</label>
              <input
                name="businessFirstName"
                type="text"
                style={{ ...inputStyle, backgroundColor: '#F8FAFC' }}
                value={businessFirstName}
                onChange={onChange}
                placeholder="Text"
                required
              />
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Last Name</label>
              <input
                name="businessLastName"
                type="text"
                style={{ ...inputStyle, backgroundColor: '#F8FAFC' }}
                value={businessLastName}
                onChange={onChange}
                placeholder="Text"
                required
              />
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Email</label>
              <input
                name="businessEmail"
                type="email"
                style={{ ...inputStyle, backgroundColor: '#F8FAFC' }}
                value={businessEmail}
                onChange={onChange}
                placeholder="Text"
                required
              />
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Password</label>
              <div style={{ position: 'relative', width: '80%' }}>
                <input
                  name="businessPassword"
                  type={showPassword ? 'text' : 'password'}
                  style={{ ...inputStyle, backgroundColor: '#F8FAFC', paddingRight: '40px', width: '100%' }}
                  value={businessPassword}
                  onChange={onChange}
                  placeholder="Text"
                  required
                />
                <span onClick={() => setShowPassword(!showPassword)} style={iconStyle}>
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            </div>
            <div style={{ ...formGroupStyle, borderBottom: 'none' }}>
              <label style={labelStyle}>Confirm Password</label>
              <div style={{ position: 'relative', width: '80%' }}>
                <input
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  style={{ ...inputStyle, backgroundColor: '#F8FAFC', paddingRight: '40px', width: '100%' }}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Text"
                  required
                />
                <span onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={iconStyle}>
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            </div>
          </Form>
        </div>

        <div style={buttonContainerStyle}>
          <Button
            type="submit"
            style={saveButtonStyle}
            onClick={handleSubmit}
            disabled={buttonDisabled}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

// Inline CSS
const pageWrapStyle = {
  padding: '18px 0px',
  fontFamily: 'Poppins',
};

const outerCardStyle = {
  width: '100%',
  maxWidth: '60%',
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '20px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
};

const innerCardStyle = {
  backgroundColor: 'white',
  borderRadius: '8px',
  marginBottom: '20px',
  border: '1px solid #E1E0E2',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  overflow: 'hidden',
};

const sectionHeaderStyle = {
  padding: '20px 20px 10px 20px',
};

const sectionHeadingStyle = {
  margin: 0,
  fontSize: '18px',
  fontWeight: 600,
  color: '#1c2c36',
  fontFamily: 'Poppins',
};

const underlineStyle = {
  marginTop: '8px',
  height: '1px',
  backgroundColor: '#27B99A',
  width: '100%',
};

const formGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  padding: '5px 20px 10px 20px',
};

const labelStyle = {
  fontWeight: 600,
  fontSize: '14px',
  color: '#1c2c36',
  margin: 0,
  marginBottom: '8px',
  fontFamily: 'Poppins',
};

const inputStyle = {
  width: '80%',
  padding: '10px 12px',
  borderRadius: '4px',
  backgroundColor: '#F8FAFC',
  background: '#F8FAFC',
  border: 'none',
  borderWidth: '0',
  boxShadow: 'none',
  WebkitBoxShadow: 'none',
  MozBoxShadow: 'none',
  fontSize: '14px',
  color: '#1c2c36',
  fontFamily: 'Poppins',
  outline: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  appearance: 'none',
};

const solutionsProAccessStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '15px 20px',
  marginBottom: '20px',
  backgroundColor: 'white',
  borderRadius: '8px',
  border: '1px solid #E1E0E2',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const toggleSwitchStyle = {
  fontSize: '18px',
};

const buttonContainerStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '20px',
};

const saveButtonStyle = {
  backgroundColor: '#27B99A',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  fontSize: '16px',
  fontWeight: 600,
  padding: '10px 30px',
  cursor: 'pointer',
  fontFamily: 'Poppins',
};

const iconStyle = {
  position: 'absolute',
  right: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  cursor: 'pointer',
  color: '#8E8E93',
  fontSize: '16px',
};

export default AddBusiness;
